import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { mappingRulesService } from '@/services/mapping-rules.service';
import {
  CreateMappingRuleData,
  UpdateMappingRuleData,
  MappingRuleFilters,
} from '@/types/mapping-rules';
import { teamQueryKeys } from '@/lib/queryKeys';
import { useEffectiveTeamId } from '@/hooks/useEffectiveTeamId';

/**
 * Calendar Mapping Rules Hooks
 *
 * React Query hooks for calendar mapping rule management
 * Used for auto-distribution of post variations to target calendars
 */

/**
 * Fetch all mapping rules with optional filters
 */
export const useMappingRules = (filters?: MappingRuleFilters) =>
  useQuery({
    queryKey: ['mapping-rules', filters],
    queryFn: () => mappingRulesService.getMappingRules(filters),
  });

/**
 * Fetch single mapping rule by ID
 */
export const useMappingRule = (id: number) =>
  useQuery({
    queryKey: ['mapping-rule', id],
    queryFn: () => mappingRulesService.getMappingRule(id),
    enabled: !!id,
  });

/**
 * Fetch mapping rules for a specific calendar
 */
export const useMappingRulesByCalendar = (calendarId: number) =>
  useQuery({
    queryKey: ['mapping-rules', 'by-calendar', calendarId],
    queryFn: () => mappingRulesService.getMappingRulesByCalendar(calendarId),
    enabled: !!calendarId,
  });

/**
 * Create new mapping rule
 */
export const useCreateMappingRule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateMappingRuleData) => mappingRulesService.createMappingRule(data),
    onSuccess: () => {
      // Invalidate all mapping rule queries to refetch
      queryClient.invalidateQueries({ queryKey: ['mapping-rules'] });
      queryClient.invalidateQueries({ queryKey: ['mapping-rule'] });
    },
  });
};

/**
 * Update mapping rule
 */
export const useUpdateMappingRule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateMappingRuleData }) =>
      mappingRulesService.updateMappingRule(id, data),
    onSuccess: (_, variables) => {
      // Invalidate mapping rule queries
      queryClient.invalidateQueries({ queryKey: ['mapping-rules'] });
      queryClient.invalidateQueries({ queryKey: ['mapping-rule', variables.id] });
    },
  });
};

/**
 * Delete mapping rule
 */
export const useDeleteMappingRule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => mappingRulesService.deleteMappingRule(id),
    onSuccess: () => {
      // Invalidate mapping rule queries
      queryClient.invalidateQueries({ queryKey: ['mapping-rules'] });
      queryClient.invalidateQueries({ queryKey: ['mapping-rule'] });
      queryClient.invalidateQueries({ queryKey: ['calendar-mappings'] });
    },
  });
};

/**
 * Batch save rules for a calendar (replaces all existing rules)
 */
export const useSaveRulesForCalendar = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sourceCalendarId,
      rules,
    }: {
      sourceCalendarId: number;
      rules: Array<{ targetCalendarId: number; variationTitle: string }>;
    }) => mappingRulesService.saveRulesForCalendar(sourceCalendarId, rules),
    onSuccess: () => {
      // Invalidate all mapping rule queries
      queryClient.invalidateQueries({ queryKey: ['mapping-rules'] });
      queryClient.invalidateQueries({ queryKey: ['mapping-rule'] });
      // Invalidate calendar mappings
      queryClient.invalidateQueries({ queryKey: ['calendar-mappings'] });
    },
  });
};

/**
 * Apply rules retroactively to existing posts
 */
export const useApplyRulesRetroactive = () => {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  return useMutation({
    mutationFn: ({
      sourceCalendarId,
      dateFrom,
      dateTo,
    }: {
      sourceCalendarId: number;
      dateFrom?: Date;
      dateTo?: Date;
    }) => mappingRulesService.applyRulesRetroactive(sourceCalendarId, dateFrom, dateTo),
    onSuccess: () => {
      // Invalidate calendar mappings and calendar posts since rules were applied
      queryClient.invalidateQueries({ queryKey: ['calendar-mappings'] });
      queryClient.invalidateQueries({ queryKey: teamQueryKeys.calendarPosts.all(teamId) });
    },
  });
};

/**
 * Get unique variation titles for autocomplete
 */
export const useVariationTitles = (search?: string) =>
  useQuery({
    queryKey: ['variation-titles', search],
    queryFn: () => mappingRulesService.getVariationTitles(search),
  });
